<template>
  <vs-popup 
    :active.sync="active"
    title="Manage User Storage Limits"
    class="user-limits-modal"
  >
    <div class="p-6">
      <!-- User Search -->
      <div class="mb-6">
        <label class="block text-sm font-medium mb-2">Select User</label>
        <vs-select 
          v-model="selectedUserId" 
          placeholder="Search and select user..."
          class="w-full"
          filterable
          @search="searchUsers"
          @change="loadUserLimits"
        >
          <vs-select-item 
            v-for="user in users" 
            :key="user.id" 
            :value="user.id" 
            :text="`${user.first_name} ${user.last_name} (${user.email})`" 
          />
        </vs-select>
      </div>

      <!-- User Limits Form -->
      <div v-if="selectedUserId && userLimits">
        <div class="mb-4 p-4 bg-grey-light rounded">
          <h4 class="text-sm font-medium mb-2">Current Usage</h4>
          <div class="text-sm">
            <div>Active Accounts: {{ userLimits.current_usage.accounts_count }}</div>
            <div>Total Quota Used: {{ userLimits.current_usage.total_quota_gb }} GB</div>
            <div>Has Custom Limits: {{ userLimits.has_custom_limits ? 'Yes' : 'No' }}</div>
          </div>
        </div>

        <form @submit.prevent="updateUserLimits">
          <!-- Max Accounts -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-2">Maximum Accounts</label>
            <vs-input 
              v-model="form.max_accounts"
              type="number"
              min="0"
              max="50"
              placeholder="Enter maximum accounts..."
              class="w-full"
              :state="errors.max_accounts ? 'danger' : null"
            />
            <small v-if="errors.max_accounts" class="text-danger">{{ errors.max_accounts }}</small>
            <small class="text-grey">Current: {{ userLimits.limits.max_accounts }}</small>
          </div>

          <!-- Max Quota GB -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-2">Maximum Total Quota (GB)</label>
            <vs-input 
              v-model="form.max_quota_gb"
              type="number"
              min="0"
              max="10000"
              placeholder="Enter maximum quota..."
              class="w-full"
              :state="errors.max_quota_gb ? 'danger' : null"
            />
            <small v-if="errors.max_quota_gb" class="text-danger">{{ errors.max_quota_gb }}</small>
            <small class="text-grey">Current: {{ userLimits.limits.max_quota_gb }} GB</small>
          </div>

          <!-- Max Containers per Account -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-2">Maximum Containers per Account</label>
            <vs-input 
              v-model="form.max_containers_per_account"
              type="number"
              min="0"
              max="1000"
              placeholder="Enter maximum containers..."
              class="w-full"
              :state="errors.max_containers_per_account ? 'danger' : null"
            />
            <small v-if="errors.max_containers_per_account" class="text-danger">{{ errors.max_containers_per_account }}</small>
            <small class="text-grey">Current: {{ userLimits.limits.max_containers_per_account }}</small>
          </div>

          <!-- Actions -->
          <div class="flex justify-end space-x-2">
            <vs-button 
              @click="resetToDefaults"
              color="warning"
              type="border"
              :disabled="!userLimits.has_custom_limits"
            >
              Reset to Defaults
            </vs-button>
            <vs-button 
              type="submit"
              color="primary"
              :loading="loading"
            >
              Update Limits
            </vs-button>
          </div>
        </form>
      </div>

      <!-- Close Button -->
      <div class="flex justify-end mt-6" v-if="!selectedUserId">
        <vs-button 
          @click="$emit('close')"
          color="danger"
          type="border"
        >
          Close
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import debounce from 'lodash/debounce'

export default {
  name: 'UserLimitsModal',
  data() {
    return {
      active: true,
      loading: false,
      users: [],
      selectedUserId: '',
      userLimits: null,
      form: {
        max_accounts: 3,
        max_quota_gb: 100,
        max_containers_per_account: 50
      },
      errors: {}
    }
  },
  watch: {
    active(newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  mounted() {
    this.searchUsers('')
  },
  methods: {
    searchUsers: debounce(async function(query) {
      try {
        const response = await this.$http.get('/admin/users', {
          params: {
            search: query,
            per_page: 50
          }
        })
        
        if (response.data.status) {
          this.users = response.data.params.data || response.data.params
        }
      } catch (error) {
        console.error('Error searching users:', error)
      }
    }, 300),

    async loadUserLimits() {
      if (!this.selectedUserId) return

      try {
        const response = await this.$http.get(`/admin/haio-smart-storage/user-limits/${this.selectedUserId}`)
        if (response.data.status) {
          this.userLimits = response.data.params
          this.form = {
            max_accounts: this.userLimits.limits.max_accounts,
            max_quota_gb: this.userLimits.limits.max_quota_gb,
            max_containers_per_account: this.userLimits.limits.max_containers_per_account
          }
        }
      } catch (error) {
        console.error('Error loading user limits:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load user limits',
          color: 'danger'
        })
      }
    },

    async updateUserLimits() {
      this.loading = true
      this.errors = {}

      try {
        const payload = {
          user_id: this.selectedUserId,
          ...this.form
        }

        const response = await this.$http.post('/admin/haio-smart-storage/user-limits', payload)
        
        if (response.data.status) {
          this.$vs.notify({
            title: 'Success',
            text: 'User limits updated successfully',
            color: 'success'
          })
          this.loadUserLimits() // Reload to show updated data
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          // Validation errors
          const message = error.response.data.message
          this.$vs.notify({
            title: 'Validation Error',
            text: message,
            color: 'danger'
          })
          
          // Set field-specific errors if available
          if (error.response.data.errors) {
            this.errors = error.response.data.errors
          }
        } else {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to update user limits',
            color: 'danger'
          })
        }
      } finally {
        this.loading = false
      }
    },

    resetToDefaults() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Reset to Defaults',
        text: 'This will remove custom limits for this user and apply global defaults. Continue?',
        accept: async () => {
          try {
            // Delete custom limits by sending a DELETE request or setting limits to null
            const response = await this.$http.delete(`/admin/haio-smart-storage/user-limits/${this.selectedUserId}`)
            
            if (response.data.status) {
              this.$vs.notify({
                title: 'Success',
                text: 'User limits reset to defaults',
                color: 'success'
              })
              this.loadUserLimits() // Reload to show default values
            }
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to reset user limits',
              color: 'danger'
            })
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.user-limits-modal {
  max-width: 600px;
}
</style>